// The automatic checks: plain rules over the case data, worked out in the
// browser. Each says pass, warn or fail with one line of why, and where to
// look. A rule that has nothing to go on is left out rather than guessed:
// no departure date, no "valid after leaving" check.
import { addMonths, dayLong, parseAnswerDate, parseStoredDate, today as todayDay, DAY } from "./format";

const SCHENGEN = "schengen_short_stay";
const SCHENGEN_MONTHS_AFTER_LEAVING = 3;
const MAX_SHORT_STAY_DAYS = 90;
const CLOSED = new Set(["approved", "rejected", "cancelled", "expired"]);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Up to three names, then "and N more".
function listNames(names) {
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 3).join(", ")} and ${names.length - 3} more`;
}

// The traveller's passport document: named so, and not the passport-size
// photo, nor a guarantor's or a host's passport copy.
export function isPassportDocument(doc) {
  const name = String(doc?.name || "");
  return /passport|passeport|جواز/i.test(name) && !/photo|picture|صورة/i.test(name) && !/guarantor|host|sponsor|inviting|كفيل|مضيف/i.test(name);
}

// Questions not linked to a field key yet (France's travel dates) are found
// by their label, the way the inbox's trip date is.
const LABELS = { arrival_date: /arrival date/i, departure_date: /departure date/i };

function fieldByKey(traveller, key) {
  const fields = traveller.sections.flatMap((s) => s.fields);
  return fields.find((f) => f.key === key) || (LABELS[key] && fields.find((f) => !f.key && LABELS[key].test(f.label || ""))) || null;
}

// The follow-ups the server emailed since the latest "Email for new copies"
// (API docs/copy-reminders.md): { count, last } (last: when, or null). After
// the second, the team calls or messages the customer.
export const COPY_REMINDERS_MAX = 2;
const time = (value) => (value ? new Date(value).getTime() : NaN);
export function copyRemindersSent(caseData) {
  const asked = (caseData.travellers || [])
    .flatMap((t) => t.documents || [])
    .filter((d) => d.upload?.review_status === "needs_new" && d.upload.requested_at)
    .map((d) => time(d.upload.requested_at))
    .filter((t) => !Number.isNaN(t));
  if (!asked.length) return { count: 0, last: null };
  const since = Math.max(...asked);
  const sent = (caseData.events || []).filter((e) => e.type === "email" && e.detail?.kind === "copy_reminder" && e.detail?.sent && time(e.created_at) >= since);
  return { count: sent.length, last: sent.length ? sent[sent.length - 1].created_at : null };
}

const fieldTarget = (field) => (field ? { type: "field", questionId: field.question_id, key: field.key } : null);
const docTarget = (doc) => (doc ? { type: "doc", documentId: doc.document_id } : null);

// The passport's expiry: the date recorded with the uploaded copy first, the
// traveller's answer second.
function passportExpiry(traveller) {
  const doc = traveller.documents.find(isPassportDocument) || null;
  const fromCopy = parseStoredDate(doc?.upload?.expire_at);
  const field = fieldByKey(traveller, "passport_expiry_date");
  const fromAnswer = parseAnswerDate(field?.value);
  if (fromCopy != null) return { day: fromCopy, source: "copy", doc, field, fromAnswer };
  if (fromAnswer != null) return { day: fromAnswer, source: "answer", doc, field, fromAnswer };
  return { day: null, source: null, doc, field, fromAnswer };
}

// Every check for one traveller, in the order an agent works.
// Names folded for matching ("United Arab Emirates (the)", "UAE").
const foldName = (text) =>
  String(text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\(the\)|^the\s+/g, "")
    .replace(/[^a-z ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
const COUNTRY_ALIASES = { uae: "AE", emirates: "AE", ksa: "SA", saudi: "SA", uk: "GB", britain: "GB", usa: "US", us: "US", turkey: "TR" };

// "Yes" / "Oui" / "نعم" -> true, "No" -> false, else undefined.
function yesNo(value) {
  const text = foldName(value);
  if (/^(yes|y|true|oui)$/.test(text) || String(value ?? "").trim() === "نعم") return true;
  if (/^(no|n|false|non)$/.test(text) || String(value ?? "").trim() === "لا") return false;
  return undefined;
}

/**
 * The "Who can apply" check from the traveller's answers (country of
 * residence, the visa they hold and what it is) against the product's
 * eligibility: a check, or null when the product has none.
 *   trip: { arrival, departure } as days, for "still valid on arrival"
 */
export function whoCanApply(eligibility, fields, trip = {}) {
  if (!eligibility) return null;
  const answer = (key) => fields.find((f) => f.key === key);
  const residenceField = answer("country_of_residence");
  const heldField = answer("held_visa");
  const title = "Who can apply";
  const target = fieldTarget(residenceField || heldField);
  const lives = (() => {
    if (!eligibility.residence?.length) return null;
    if (residenceField?.value == null) return undefined;
    const text = foldName(residenceField.value);
    const words = text.split(" ");
    const codes = eligibility.residence.map((c) => c.code);
    const named = eligibility.residence.find((c) => foldName(c.name) === text || text.endsWith(` ${foldName(c.name)}`));
    const alias = COUNTRY_ALIASES[text] || COUNTRY_ALIASES[words[words.length - 1]];
    return Boolean(named || (alias && codes.includes(alias)));
  })();
  const holds = (() => {
    if (!eligibility.held_visas?.length) return null;
    if (heldField?.value == null) return undefined;
    const text = foldName(heldField.value);
    const visa = eligibility.held_visas.find((v) => foldName(v.label) === text || v.value === heldField.value);
    if (!visa) return false;
    // What the visa must be: multiple-entry, used, still valid (API docs/eligibility.md).
    const rules = eligibility.held_rules;
    if (!rules) return true;
    const checks = [];
    if (/_visa$/.test(visa.value)) {
      if (rules.multiple) checks.push(yesNo(answer("held_visa_multiple")?.value));
      if (rules.used) checks.push(yesNo(answer("held_visa_used")?.value));
    }
    if (rules.valid) {
      const ends = parseAnswerDate(answer("held_visa_expiry_date")?.value);
      const needed = rules.valid === "departure" ? trip.departure ?? trip.arrival : trip.arrival;
      checks.push(ends != null && needed != null ? ends >= needed : undefined);
    }
    if (checks.includes(false)) return false;
    return checks.includes(undefined) ? undefined : true;
  })();
  const needed = [lives, holds].filter((v) => v !== null);
  if (!needed.length) return null;
  const known = needed.filter((v) => v !== undefined);
  const detail = (key, words) => (answer(key)?.value != null ? `${words}: ${answer(key).value}` : null);
  const said = [
    residenceField?.value != null ? `lives in ${residenceField.value}` : null,
    heldField?.value != null ? `holds: ${heldField.value}` : null,
    detail("held_visa_multiple", "multiple-entry"),
    detail("held_visa_used", "used"),
    detail("held_visa_expiry_date", "expires"),
  ]
    .filter(Boolean)
    .join("; ");
  const ok = eligibility.match === "all" ? (known.includes(false) ? false : known.length < needed.length ? null : true) : known.includes(true) ? true : known.length < needed.length ? null : false;
  if (ok === null) {
    const missing = !residenceField && eligibility.residence?.length ? "where they live" : !heldField && eligibility.held_visas?.length ? "which visa they hold" : null;
    return {
      id: "who-can-apply",
      level: "warn",
      title,
      reason: missing ? `This visa is for: ${eligibility.words} The form doesn’t ask ${missing}: add the questions for who can apply to the product.` : `This visa is for: ${eligibility.words} Not answered yet.`,
      target,
    };
  }
  if (!ok) return { id: "who-can-apply", level: "fail", title, reason: `This visa is for: ${eligibility.words} The traveller ${said}.`, target };
  return { id: "who-can-apply", level: "pass", title, reason: `The traveller ${said}.` };
}

export function travellerChecks(traveller, caseData, now = todayDay()) {
  const checks = [];
  const add = (check) => checks.push(check);
  const fields = traveller.sections.flatMap((s) => s.fields);
  const schengen = caseData.official_form?.key === SCHENGEN;

  // 1. Required answers.
  const required = fields.filter((f) => f.required);
  const missingAnswers = required.filter((f) => f.value == null);
  if (required.length) {
    add(
      missingAnswers.length
        ? {
            id: "answers",
            level: "fail",
            title: "Required answers",
            reason: `${plural(missingAnswers.length, "missing", "missing")}: ${listNames(missingAnswers.map((f) => f.label))}`,
            target: fieldTarget(missingAnswers[0]),
          }
        : { id: "answers", level: "pass", title: "Required answers", reason: `All ${required.length} given.` },
    );
  }

  // 2. Required documents uploaded.
  const requiredDocs = traveller.documents.filter((d) => d.required);
  const notUploaded = requiredDocs.filter((d) => !d.upload);
  if (requiredDocs.length) {
    add(
      notUploaded.length
        ? {
            id: "documents",
            level: "fail",
            title: "Required documents",
            reason: `Not uploaded: ${listNames(notUploaded.map((d) => d.name))}`,
            target: docTarget(notUploaded[0]),
          }
        : { id: "documents", level: "pass", title: "Required documents", reason: `All ${requiredDocs.length} uploaded.` },
    );
  }

  // 3. Reviewed by the team.
  const uploads = traveller.documents.filter((d) => d.upload);
  if (uploads.length && caseData.workspace_ready !== false) {
    const toReview = uploads.filter((d) => !d.upload.review_status);
    const newCopy = uploads.filter((d) => d.upload.review_status === "needs_new");
    if (toReview.length || newCopy.length) {
      const parts = [];
      if (toReview.length) parts.push(`${plural(toReview.length, "to review", "to review")}: ${listNames(toReview.map((d) => d.name))}`);
      // The two states of a new copy, named as everywhere (QA nadia-15).
      const notSent = newCopy.filter((d) => !d.upload.requested_at);
      const asked = newCopy.filter((d) => d.upload.requested_at);
      if (notSent.length) parts.push(`new copy needed, not sent yet: ${listNames(notSent.map((d) => d.name))}`);
      if (asked.length) parts.push(`new copy asked, waiting for the customer: ${listNames(asked.map((d) => d.name))}`);
      // Reminded by email as often as the server does, and still nothing.
      const unanswered = newCopy.length > 0 && copyRemindersSent(caseData).count >= COPY_REMINDERS_MAX;
      if (unanswered) parts.push("reminded twice by email, no new copy yet: call or message the customer");
      add({
        id: "review",
        level: unanswered ? "fail" : "warn",
        title: "Documents reviewed",
        reason: parts.join("; "),
        target: docTarget(toReview[0] || newCopy[0]),
      });
    } else {
      add({ id: "review", level: "pass", title: "Documents reviewed", reason: `All ${uploads.length} approved.` });
    }
  }

  // 4. Each document with a minimum validity, counted from today.
  const passport = passportExpiry(traveller);
  for (const doc of traveller.documents) {
    if (!doc.min_valid_months || !doc.upload) continue;
    const months = doc.min_valid_months;
    const title = `${doc.name} valid ${plural(months, "month", "months")} from today`;
    let expiry = parseStoredDate(doc.upload.expire_at);
    let fromAnswer = false;
    if (expiry == null && isPassportDocument(doc) && passport.fromAnswer != null) {
      expiry = passport.fromAnswer;
      fromAnswer = true;
    }
    const target = docTarget(doc);
    if (expiry == null) {
      add({ id: `validity-${doc.document_id}`, level: "warn", title, reason: "No expiry date recorded. Check it on the document.", target });
      continue;
    }
    const needed = addMonths(now, months);
    const source = fromAnswer ? " (from the answer)" : "";
    if (expiry < now) {
      add({ id: `validity-${doc.document_id}`, level: "fail", title, reason: `Expired on ${dayLong(expiry)}${source}.`, target });
    } else if (expiry < needed) {
      add({
        id: `validity-${doc.document_id}`,
        level: "fail",
        title,
        reason: `Valid until ${dayLong(expiry)}${source}; must reach ${dayLong(needed)}.`,
        target,
      });
    } else {
      add({ id: `validity-${doc.document_id}`, level: "pass", title, reason: `Valid until ${dayLong(expiry)}${source}.`, target });
    }
  }

  // 5. The copy's expiry and the traveller's answer agree.
  const copyExpiry = parseStoredDate(passport.doc?.upload?.expire_at);
  if (copyExpiry != null && passport.fromAnswer != null) {
    add(
      copyExpiry === passport.fromAnswer
        ? { id: "passport-match", level: "pass", title: "Passport expiry matches the answer", reason: `Both say ${dayLong(copyExpiry)}.` }
        : {
            id: "passport-match",
            level: "warn",
            title: "Passport expiry matches the answer",
            reason: `The copy says ${dayLong(copyExpiry)}, the answer ${dayLong(passport.fromAnswer)}.`,
            target: fieldTarget(passport.field),
          },
    );
  }

  const arrivalField = fieldByKey(traveller, "arrival_date");
  const departureField = fieldByKey(traveller, "departure_date");
  const arrival = parseAnswerDate(arrivalField?.value);
  const departure = parseAnswerDate(departureField?.value);
  const passportTarget = passport.source === "copy" ? docTarget(passport.doc) : fieldTarget(passport.field);

  // 6. Passport still valid for the months the visa asks: after arrival,
  // or after leaving when the product's rule says so (migration 017; 0
  // months after leaving: valid for the whole stay).
  const passportRule = passport.doc?.validity || null;
  const fromLeaving = passportRule?.from === "departure";
  if (passport.day != null && !fromLeaving && arrival != null) {
    const months = passportRule ? passportRule.months : passport.doc?.min_valid_months || 0;
    const needed = months ? addMonths(arrival, months) : arrival;
    const title = months ? `Passport valid ${plural(months, "month", "months")} after arrival` : "Passport valid on arrival";
    add(
      passport.day < needed
        ? {
            id: "passport-arrival",
            level: "fail",
            title,
            reason: `Valid until ${dayLong(passport.day)}; must reach ${dayLong(needed)} (arrival ${dayLong(arrival)}).`,
            target: passportTarget,
          }
        : { id: "passport-arrival", level: "pass", title, reason: `Valid until ${dayLong(passport.day)}; arrival ${dayLong(arrival)}.` },
    );
  }
  if (passport.day != null && fromLeaving && departure != null) {
    const months = passportRule.months || 0;
    const needed = months ? addMonths(departure, months) : departure;
    const title = months ? `Passport valid ${plural(months, "month", "months")} after leaving` : "Passport valid for the whole stay";
    add(
      passport.day < needed
        ? {
            id: "passport-leaving",
            level: "fail",
            title,
            reason: `Valid until ${dayLong(passport.day)}; must reach ${dayLong(needed)} (leaving ${dayLong(departure)}).`,
            target: passportTarget,
          }
        : { id: "passport-leaving", level: "pass", title, reason: `Valid until ${dayLong(passport.day)}; leaving ${dayLong(departure)}.` },
    );
  }

  // 7. Schengen: valid 3 months after leaving. Only with a departure date,
  // and not when the product's own rule already counts from leaving.
  if (schengen && !fromLeaving && passport.day != null && departure != null) {
    const needed = addMonths(departure, SCHENGEN_MONTHS_AFTER_LEAVING);
    const title = "Passport valid 3 months after leaving (Schengen)";
    add(
      passport.day < needed
        ? {
            id: "schengen-departure",
            level: "fail",
            title,
            reason: `Valid until ${dayLong(passport.day)}; must reach ${dayLong(needed)} (leaving ${dayLong(departure)}).`,
            target: passportTarget,
          }
        : { id: "schengen-departure", level: "pass", title, reason: `Valid until ${dayLong(passport.day)}; leaving ${dayLong(departure)}.` },
    );
  }

  // 7b. Applied inside the visa's window (migration 017): at most and at
  // least so many days before arrival, counted from the day it was
  // submitted (from today while it is still being filled in).
  const rules = caseData.visa_rules || null;
  const earliest = rules?.apply_earliest_days;
  const latest = rules?.apply_latest_days;
  if (arrival != null && (earliest != null || latest != null) && !CLOSED.has(caseData.status)) {
    const applied = caseData.status === "incomplete" ? now : parseStoredDate(caseData.submitted_at) ?? now;
    const before = Math.round((arrival - applied) / DAY);
    const when = caseData.status === "incomplete" ? "Today is" : "Submitted";
    const title = "Applied in the visa’s window";
    const window = [earliest != null ? `at most ${plural(earliest, "day", "days")}` : null, latest != null ? `at least ${plural(latest, "day", "days")}` : null]
      .filter(Boolean)
      .join(" and ");
    if (earliest != null && before > earliest) {
      add({
        id: "apply-window",
        level: "warn",
        title,
        reason: `${when} ${plural(before, "day", "days")} before arrival; this visa takes applications ${window} before. It can go in from ${dayLong(arrival - earliest * DAY)}.`,
        target: fieldTarget(arrivalField),
      });
    } else if (latest != null && before < latest) {
      add({
        id: "apply-window",
        level: "fail",
        title,
        reason: `${when} ${plural(Math.max(before, 0), "day", "days")} before arrival; this visa needs ${window} before.`,
        target: fieldTarget(arrivalField),
      });
    } else {
      add({ id: "apply-window", level: "pass", title, reason: `${plural(before, "day", "days")} before arrival; this visa takes applications ${window} before.` });
    }
  }

  // 7c. Someone this visa is for (migration 019): where they live, a visa
  // they hold, as the product's "Who can apply" says.
  const who = whoCanApply(caseData.eligibility, fields, { arrival, departure });
  if (who) add(who);

  // 8. Arrival still ahead (not asked of closed cases).
  if (arrival != null && !CLOSED.has(caseData.status)) {
    const days = Math.round((arrival - now) / DAY);
    add(
      arrival < now
        ? {
            id: "arrival-past",
            level: "fail",
            title: "Arrival date ahead",
            reason: `${dayLong(arrival)} has already passed.`,
            target: fieldTarget(arrivalField),
          }
        : {
            id: "arrival-past",
            level: "pass",
            title: "Arrival date ahead",
            reason: days === 0 ? `${dayLong(arrival)}, today.` : `${dayLong(arrival)}, in ${plural(days, "day", "days")}.`,
          },
    );
  }

  // 9 and 10. Leaving after arriving, and the length of the stay.
  if (arrival != null && departure != null) {
    if (departure < arrival) {
      add({
        id: "trip-order",
        level: "fail",
        title: "Departure after arrival",
        reason: `Departure ${dayLong(departure)} is before arrival ${dayLong(arrival)}.`,
        target: fieldTarget(departureField),
      });
    } else {
      add({ id: "trip-order", level: "pass", title: "Departure after arrival", reason: `${dayLong(arrival)} to ${dayLong(departure)}.` });
      // Days of stay count the day of arrival and the day of departure.
      const days = Math.round((departure - arrival) / DAY) + 1;
      add(
        days > MAX_SHORT_STAY_DAYS
          ? {
              id: "trip-length",
              level: schengen ? "fail" : "warn",
              title: `Stay of ${MAX_SHORT_STAY_DAYS} days or less`,
              reason: schengen
                ? `${days} days. A short-stay Schengen visa covers at most ${MAX_SHORT_STAY_DAYS}.`
                : `${days} days. Check the visa allows a stay this long.`,
              target: fieldTarget(departureField),
            }
          : {
              id: "trip-length",
              level: "pass",
              title: `Stay of ${MAX_SHORT_STAY_DAYS} days or less`,
              reason: `${days} days, counting arrival and departure days.`,
            },
      );
    }
  }

  // Checks that could not run say so: never a silent skip, never "all
  // passed" over them. A date question without a field key ("not linked")
  // is the usual reason; a typed answer that is not a date is the other.
  const why = (field, what) =>
    !field
      ? `we don’t know the ${what} (the product’s question isn’t marked as the ${what})`
      : field.value == null
      ? `no ${what} answered`
      : `the ${what} answer is not a date (DD/MM/YYYY)`;
  const skip = (id, title, reason) => add({ id, level: "skip", title, reason: `Not checked: ${reason}.` });
  if (!fromLeaving && arrival == null) skip("passport-arrival", "Passport valid on arrival", why(arrivalField, "arrival date"));
  if (fromLeaving && departure == null) skip("passport-leaving", "Passport valid after leaving", why(departureField, "departure date"));
  if (schengen && !fromLeaving && departure == null) {
    skip("schengen-departure", `Passport valid ${SCHENGEN_MONTHS_AFTER_LEAVING} months after leaving`, why(departureField, "departure date"));
  }
  if (arrival == null || departure == null) {
    skip(
      "trip-length",
      `Stay of ${MAX_SHORT_STAY_DAYS} days or less`,
      arrival == null ? why(arrivalField, "arrival date") : why(departureField, "departure date"),
    );
  }

  // 11. Date differences the customer said are intended, with their note.
  for (const note of traveller.date_notes || []) add(dateNoteCheck(note, { arrival, departure }));

  return checks;
}

// A date warning the customer said is intended before paying, with their
// note (the API's dateNotes.js, DE-02): shown as noted, beside what the
// letter and the trip said then, never as a problem. When the trip's dates
// have changed since, that is said too. arrival, departure: the trip's
// answers now (calendar days, or null).
export function dateNoteCheck(note, { arrival = null, departure = null } = {}) {
  const day = (value) => parseStoredDate(value);
  const names = (note.names || []).filter(Boolean);
  const what = names.length ? listNames(names) : "A document";
  const [from, to, then, until] = [day(note.from), day(note.to), day(note.arrival), day(note.departure)];
  const span = (a, b) => (a != null && b != null ? `${dayLong(a)} to ${dayLong(b)}` : dayLong(a ?? b));
  const said =
    note.problem === "arrival"
      ? `The flight arrives on ${dayLong(from)}; the arrival date was ${dayLong(then)}.`
      : note.problem === "return"
      ? `The return flight leaves on ${dayLong(to)}; the departure date was ${dayLong(until)}.`
      : `${names.length > 1 ? "They say" : "It says"} ${span(from, to)}; the trip was ${span(then, until)}.`;
  const words = note.note ? `The customer says this is intended: “${note.note}”` : "The customer says this is intended (no note).";
  const moved = (arrival != null && then != null && arrival !== then) || (departure != null && until != null && departure !== until);
  return {
    id: `dates-${(note.document_ids || []).join("-")}-${note.problem}`,
    level: "noted",
    title: `${what}: dates and the trip`,
    reason: `${said} ${words}${moved ? ` The trip has changed since: now ${span(arrival, departure)}.` : ""}`,
    target: note.document_ids?.length ? { type: "doc", documentId: note.document_ids[0] } : null,
  };
}

// Every traveller's checks, with totals per level.
export function caseChecks(caseData, now = todayDay()) {
  const travellers = caseData.travellers.map((traveller, index) => ({
    index,
    requestId: traveller.request_id,
    name: traveller.name || `Traveller ${traveller.number}`,
    checks: travellerChecks(traveller, caseData, now),
  }));
  const all = travellers.flatMap((t) => t.checks);
  return {
    travellers,
    totals: {
      fail: all.filter((c) => c.level === "fail").length,
      warn: all.filter((c) => c.level === "warn").length,
      pass: all.filter((c) => c.level === "pass").length,
      skip: all.filter((c) => c.level === "skip").length,
      noted: all.filter((c) => c.level === "noted").length,
    },
  };
}

// One traveller's progress, for the summary above their answers and on tabs.
export function travellerProgress(traveller) {
  const fields = traveller.sections.flatMap((s) => s.fields);
  const required = fields.filter((f) => f.required);
  const requiredDocs = traveller.documents.filter((d) => d.required);
  const uploads = traveller.documents.filter((d) => d.upload);
  return {
    answers: { required: required.length, given: required.filter((f) => f.value != null).length, missing: traveller.missing.answers },
    documents: { required: requiredDocs.length, uploaded: requiredDocs.filter((d) => d.upload).length, missing: traveller.missing.documents },
    review: {
      uploads: uploads.length,
      approved: uploads.filter((d) => d.upload.review_status === "approved").length,
      toReview: uploads.filter((d) => !d.upload.review_status).length,
      newCopy: uploads.filter((d) => d.upload.review_status === "needs_new").length,
    },
  };
}

// The trip date the inbox shows (cases/list travel_date): the arrival answer,
// by its field key, else by a question labelled "arrival date".
export function travelAnswer(caseData) {
  if (caseData.travel_date) return caseData.travel_date;
  for (const traveller of caseData.travellers || []) {
    if (traveller.travel_date) return traveller.travel_date;
    const field = fieldByKey(traveller, "arrival_date");
    if (field?.value) return field.value;
  }
  return null;
}

// What the AI document check found on an upload (the API's
// docs/document-check.md), for the agent; null when there is nothing to say
// (no check, or a check of a file since replaced). It is never a decision:
// the agent still opens the file. `reason` is what "Ask for a new copy"
// starts with: the issues as the customer would read them, to edit.
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
export function aiCheckView(upload) {
  const check = upload?.ai_check;
  if (!check || !upload.file || check.file !== upload.file) return null;
  const typed = String(upload.expire_at || "").slice(0, 10);
  const printed = String(check.printed_expiry || "").slice(0, 10);
  const expiry = ISO_DAY.test(printed) && ISO_DAY.test(typed) && printed !== typed ? { printed, typed } : null;
  const issues = (Array.isArray(check.issues) ? check.issues : []).filter((i) => i && typeof i.text === "string" && i.text.trim());
  const ok = check.verdict === "looks_right" && !expiry;
  const sure = issues.filter((i) => i.kind !== "check");
  return {
    ok,
    label: ok
      ? "AI check: looks like the right document"
      : check.verdict === "unclear"
      ? "AI check: couldn’t read it clearly"
      : check.verdict === "problems"
      ? "AI check: possible problem"
      : "AI check: worth a look",
    issues: issues.map((i) => i.text),
    note: check.staff_note || null,
    expiry,
    reason: ok ? "" : (sure.length ? sure : issues).map((i) => i.text).join(" ").slice(0, 500),
  };
}
